import { defineStore } from 'pinia';
import { REGION_TREE } from '@/config/region-index';
import { recordVisit } from '@/utils/visitTracker';

interface RegionConfig {
  code: string;
  name: string;
  level: 'province' | 'city' | 'county';
  map: string;
  tagline: string;
  accountUrl: string | null;
  accountTitle: string | null;
  leadHook: { chips: string[]; label: string; value: string };
}

interface ManifestEntry {
  path: string;
  folder: string;
  dataUrl: string;
}

interface Manifest {
  generated_at: string;
  count: number;
  regions: ManifestEntry[];
}

/**
 * 手写行政区划条目（regions-dict.json）
 * - 不依赖 geojson / data 文件扫描
 * - 直接列出 21 个市 + 已资料的县区
 * - 新增区域只需编辑该 json
 */
interface AdminDivision {
  code: string;
  name: string;
  level: 'city' | 'county';
  cityCode: string;
  parentCity: string;
  hasAccount: boolean;
  plateLetter?: string; // 车牌字母（A-W），用于排序
}
interface AdminDivisions {
  generated_at: string;
  regions: AdminDivision[];
}

/**
 * 拼音 code → 中文 name 的统一映射（不依赖目录命名）
 * 新加区域时若用拼音目录，可在此登记
 */
const PINYIN_TO_NAME: Record<string, string> = {
  yangjiang: '阳江市',
  yangchun: '阳春市',
  jieyang: '揭阳市',
  zhanjiang: '湛江市',
  rongcheng: '榕城区',
  jiedong: '揭东区',
  jiexi: '揭西县',
  puning: '普宁市',
  huilai: '惠来县'
};

interface RegionState {
  current: string;
  config: RegionConfig | null;
  manifest: ManifestEntry[];
  manifestLoaded: boolean;
  /** 手写行政区划清单（21 市 + 已资料县区） */
  adminRegions: AdminDivision[];
  adminLoaded: boolean;
}

// 通过 fetch + blob URL 加载 ES module（规避中文路径 + 构建期静态分析问题）
const _moduleCache = new Map<string, { url: string; mod: { default: RegionConfig } }>();
async function importDataJs(dataUrl: string): Promise<{ default: RegionConfig }> {
  const hit = _moduleCache.get(dataUrl);
  if (hit) return hit.mod;
  const res = await fetch(dataUrl);
  if (!res.ok) throw new Error(`加载 ${dataUrl} 失败：HTTP ${res.status}`);
  const code = await res.text();
  const cleaned = code.replace(/^\uFEFF/, '');
  const blob = new Blob([cleaned], { type: 'application/javascript' });
  const url = URL.createObjectURL(blob);
  const mod = await import(/* @vite-ignore */ url);
  _moduleCache.set(dataUrl, { url, mod });
  return mod;
}

/**
 * manifest path → region（最终 code 统一为中文行政区名）
 * - 路径 1 段：按 name 结尾推断（`市` → 市级；`县/区` → 县级）
 * - 路径 2 段（`市级/全市` 或 `市级/市级`）：市级
 * - 路径 2 段（`省/全省`）：province
 * - 路径 2 段（`市级/县区`）：县级
 * - 路径 3+ 段：取最后一段作为 code（county）
 */
function pathToRegion(entry: ManifestEntry): { code: string; name: string; level: 'province' | 'city' | 'county' } {
  const parts = entry.path.split('/');
  const last = parts[parts.length - 1];
  const normalize = (s: string) => PINYIN_TO_NAME[s] || s;

  if (parts.length === 1) {
    const code = normalize(last);
    const PINYIN_LEVEL: Record<string, 'province' | 'city' | 'county'> = {
      guangdong: 'province',
      yangjiang: 'city',
      yangchun:  'county',
      jieyang:   'city',
      zhanjiang: 'city'
    };
    const level: 'province' | 'city' | 'county' =
      PINYIN_LEVEL[last] || (code.endsWith('市') ? 'city' : 'county');
    return { code, name: code, level };
  }

  if (parts.length === 2) {
    const parent = parts[0];
    // 省级：`省/全省` 或 parent 本身就是省
    if (parent === '广东省' || last === '全省' || parent === 'guangdong') {
      const code = normalize(parent);
      return { code, name: code, level: 'province' };
    }
    const isCityLevel = last === '全市' || last === parent;
    if (isCityLevel) {
      const code = normalize(parent);
      return { code, name: code, level: 'city' };
    }
    const code = normalize(last);
    return { code, name: code, level: 'county' };
  }

  const code = normalize(last);
  return { code, name: code, level: 'county' };
}

function placeholder(code: string, name: string, level: 'province' | 'city' | 'county'): RegionConfig {
  return {
    code, name, level,
    map: 'assets/maps/广东省-全域.png',
    tagline: '该地区资料编制中 · 暂未列入机会账公开版',
    accountUrl: null, accountTitle: null,
    leadHook: { chips: [], label: '暂未上线', value: '敬请期待' }
  };
}

export const useRegionStore = defineStore('region', {
  state: (): RegionState => ({
    current: 'guangdong',
    config: null,
    manifest: [],
    manifestLoaded: false,
    adminRegions: [],
    adminLoaded: false
  }),
  getters: {
    isProvince(): boolean { return this.config?.level === 'province'; },

    /** 通过 code 找到 manifest entry（用于拿 dataUrl）。guangdong 不在此查询（由 load 单独处理） */
    findManifestByCode(state) {
      return (code: string): ManifestEntry | null => {
        if (code === 'guangdong') return null;
        for (const entry of state.manifest) {
          const r = pathToRegion(entry);
          if (r.code === code) return entry;
        }
        return null;
      };
    },

    /**
     * 给定市级 code，返回其直属县区 code 列表
     * - 优先 adminRegions
     * - 兜底 manifest
     */
    countiesByCity(state) {
      return (cityCode: string): string[] => {
        if (state.adminRegions.length) {
          return state.adminRegions
            .filter(r => r.level === 'county' && r.parentCity === cityCode)
            .map(r => r.code);
        }
        // 兜底 manifest
        const out: string[] = [];
        for (const entry of state.manifest) {
          const parts = entry.path.split('/');
          if (parts.length !== 2) continue;
          const parent = parts[0];
          const last = parts[1];
          const parentNorm = PINYIN_TO_NAME[parent] || parent;
          const lastNorm = PINYIN_TO_NAME[last] || last;
          if (parentNorm !== cityCode) continue;
          if (last === '全市' || last === '全省') continue;
          out.push(lastNorm);
        }
        return out;
      };
    },

    /** 通过 code 查 adminRegion */
    findAdminByCode(state) {
      return (code: string): AdminDivision | null => {
        return state.adminRegions.find(r => r.code === code) || null;
      };
    },

    /** 合并 manifest 后得到所有 region 列表（用于下拉等），按车牌字母 A→Z 排序 */
    allRegions(state): { code: string; name: string; level: string }[] {
      const out: { code: string; name: string; level: string; plateLetter?: string }[] = [
        { code: 'guangdong', name: '广东省', level: 'province', plateLetter: '·' }
      ];
      // 优先用 adminRegions（手写清单，准确稳定）
      for (const r of state.adminRegions) {
        if (out.find(o => o.code === r.code)) continue;
        out.push({ code: r.code, name: r.name, level: r.level, plateLetter: r.plateLetter });
      }
      // 兜底：manifest 推导（兼容旧的拼音目录）
      if (!state.adminRegions.length) {
        for (const entry of state.manifest) {
          const r = pathToRegion(entry);
          if (r.level === 'province' || out.find(o => o.code === r.code)) continue;
          out.push({ code: r.code, name: r.name, level: r.level });
        }
      }
      // 排序：province 固定首位；其余按 plateLetter 升序（A→Z）；同级按 name
      out.sort((a, b) => {
        if (a.code === 'guangdong') return -1;
        if (b.code === 'guangdong') return 1;
        const pa = a.plateLetter || 'Z';
        const pb = b.plateLetter || 'Z';
        if (pa !== pb) return pa.localeCompare(pb);
        // 同车牌字母内：city 在前，county 在后（按 level 排序）
        if (a.level !== b.level) return a.level === 'city' ? -1 : 1;
        return a.name.localeCompare(b.name, 'zh-CN');
      });
      return out;
    }
  },
  actions: {
    /**
     * 加载 manifest（regions-manifest.json）
     * - 启动时调用一次
     * - 重新生成 manifest 后再启动 UI: 重跑 `node scripts/build-manifest.mjs`
     */
    async loadManifest(force = false): Promise<void> {
      if (this.manifestLoaded && !force) return;
      try {
        const res = await fetch('data/regions-manifest.json?t=' + Date.now());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const m = (await res.json()) as Manifest;
        this.manifest = m.regions || [];
        this.manifestLoaded = true;
        console.log(`[region] manifest loaded: ${this.manifest.length} regions`);
      } catch (e) {
        console.warn('[region] manifest load failed:', e);
        this.manifest = [];
        this.manifestLoaded = true;
      }
    },

    /**
     * 加载手写行政区划清单（regions-dict.json）
     * - 不依赖 geojson / data 文件
     * - 新加区域只需编辑该 json
     */
    async loadAdminDivisions(force = false): Promise<void> {
      if (this.adminLoaded && !force) return;
      try {
        const res = await fetch('data/regions-dict.json?t=' + Date.now());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = (await res.json()) as AdminDivisions;
        this.adminRegions = d.regions || [];
        this.adminLoaded = true;
        console.log(`[region] admin divisions loaded: ${this.adminRegions.length} regions`);
      } catch (e) {
        console.warn('[region] admin divisions load failed:', e);
        this.adminRegions = [];
        this.adminLoaded = true;
      }
    },

    bootstrap() {
      const params = new URLSearchParams(location.search);
      const r = params.get('region');
      if (r) this.current = r;
      this.load(this.current);
    },

    async load(code: string, hintName?: string) {
      // 确保 admin / manifest 都已加载
      if (!this.adminLoaded) await this.loadAdminDivisions();
      if (!this.manifestLoaded) await this.loadManifest();

      // 省级（province）：单独从 manifest 找 '广东省/...' 条目加载
      if (code === 'guangdong') {
        const provEntry = this.manifest.find(e => e.path.startsWith('广东省/'));
        if (provEntry) {
          try {
            const mod = await importDataJs(provEntry.dataUrl);
            this.config = { ...mod.default, code: 'guangdong', name: '广东省', level: 'province' };
            return;
          } catch (e) {
            console.error('[region] load province failed:', e);
          }
        }
        this.config = placeholder('guangdong', '广东省', 'province');
        return;
      }

      // 查 admin 清单（有 hasAccount 信息）
      const admin = this.findAdminByCode(code);
      if (admin && admin.hasAccount) {
        const manifestEntry = this.findManifestByCode(code);
        if (manifestEntry) {
          try {
            const mod = await importDataJs(manifestEntry.dataUrl);
            this.config = { ...mod.default, code: admin.code, name: admin.name, level: admin.level };
            return;
          } catch (e) {
            console.error(`[region] load ${code} failed:`, e);
          }
        }
      }

      // 无 admin / 无 hasAccount / manifest 缺失 → placeholder
      const name = hintName || admin?.name || code;
      const lv: 'province' | 'city' | 'county' = admin?.level || (code.length > 0 ? 'city' : 'city');
      this.config = placeholder(admin?.code || code, name, lv);
    },

    async setRegion(code: string, hintName?: string) {
      if (code === this.current && this.config) return;
      this.current = code;
      // 区域切换：本地累计 region 列表，由首次 visit / 心跳统一落库，不再单独发请求
      recordVisit({ path: location.pathname + location.search, regionCode: code });
      await this.load(code, hintName);
    }
  }
});

// 兼容旧导出（PanelGuest 等可能还在引用）
export { REGION_TREE };